import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Linkedin, Mail } from 'lucide-react';
import { useBooking } from '../context/BookingContext';
import { LOGO_URL } from '../config/site';
import './Footer.css';

const toSection = (id) => `/#${id}`;

/** Update to your live LinkedIn company or showcase URL */
const LINKEDIN_URL = 'https://www.linkedin.com/company/cure8studios';
const CONTACT_EMAIL = 'contact@cure8studios.com';

const EXPLORE_LINKS = [
  { label: 'The Method', id: 'how-it-works' },
  { label: 'AI Assistant', id: 'ai-assistant' },
  { label: 'Packages', id: 'pricing' },
  { label: 'Why Cure8', id: 'why-cure8' },
  { label: 'Free check', id: 'diagnosis' },
  { label: 'FAQ', id: 'faq' },
];

const COMPANY_LINKS = [
  { label: 'Privacy Policy', to: '/privacy-policy', type: 'route' },
  { label: 'Terms of Service', to: '/terms-of-service', type: 'route' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { openCalendly } = useBooking();

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-col footer-col-brand">
            <Link to="/" className="footer-logo-link">
              <img
                src={LOGO_URL}
                alt="Cure8 Studios"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span className="logo-fallback-text">Cure8 Studios</span>
            </Link>
            <p className="footer-tagline">
              AI-powered workflow audits and automation for owner-operated businesses. We fix inbound response,
              follow-up, and operations so revenue stops leaking after someone reaches out.
            </p>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Explore</p>
            <ul className="footer-links">
              {EXPLORE_LINKS.map((item) => (
                <li key={item.id}>
                  <Link to={toSection(item.id)} className="footer-link">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Company</p>
            <ul className="footer-links">
              {COMPANY_LINKS.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="footer-link">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Contact</p>
            <ul className="footer-links footer-links-contact">
              <li>
                <button type="button" className="footer-link footer-link-button" onClick={openCalendly}>
                  Schedule a Discovery Call
                </button>
              </li>
              <li>
                <a href={`mailto:${CONTACT_EMAIL}`} className="footer-link">
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href="https://cure8studios.com"
                  className="footer-link footer-link-external"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  cure8studios.com
                  <ExternalLink size={12} className="footer-external-icon" aria-hidden />
                </a>
              </li>
              <li className="footer-social-row">
                <a
                  href={LINKEDIN_URL}
                  className="footer-social-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Cure8 Studios on LinkedIn"
                >
                  <Linkedin size={20} strokeWidth={2} aria-hidden />
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="footer-social-btn"
                  aria-label={`Email ${CONTACT_EMAIL}`}
                >
                  <Mail size={20} strokeWidth={2} aria-hidden />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">© {currentYear} Cure8 Studios. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
